import { wafflesKeyColumnName } from "@app/shared/waffles";
import { CreateSpecialtyGroupException } from "@app/exceptions/interfaces";
import { SpecialtyGroupExceptionStatus } from "@app/exceptions/enums";
import { RegionSetStatusType } from "@app/projects/enumerators";
import { AccessGapTypeEnum } from "@app/projects/network-assessment/provider-hub/enumerators";

export interface NetworkSubmissionFilters {
  regulatorClientIds: number[];
  regulatoryReviewStatus: SpecialtyGroupExceptionStatus | null;
  serviceAreas: string[];
  specialties: string[];
}

export interface NetworkSubmissionFilterOption {
  value: string;
  displayName: string;
  children?: NetworkSubmissionFilterOption[];
}

export interface NetworkSubmissionFilterOptions {
  serviceAreas: NetworkSubmissionFilterOption[];
  specialties: NetworkSubmissionFilterOption[];
}

export enum NetworkSubmissionWizardStep {
  SelectRegulator = 0,
  SelectProject = 1,
  AdequacyReview = 2,
  Exceptions = 3,
  Review = 4,
}

export const NetworkSubmissionWizardStepName = {
  [NetworkSubmissionWizardStep.SelectRegulator]: "Select Regulator",
  [NetworkSubmissionWizardStep.SelectProject]: "Select Project",
  [NetworkSubmissionWizardStep.AdequacyReview]: "Adequacy Review",
  [NetworkSubmissionWizardStep.Exceptions]: "Exceptions",
  [NetworkSubmissionWizardStep.Review]: "Review",
};

export const ValidExceptionStatuses = [
  SpecialtyGroupExceptionStatus.Pending,
  SpecialtyGroupExceptionStatus.Submitted,
  SpecialtyGroupExceptionStatus.Approved,
  SpecialtyGroupExceptionStatus.Denied,
  SpecialtyGroupExceptionStatus.Appealed,
  SpecialtyGroupExceptionStatus.Expired
];

export interface NetworkSubmissionProject {
  id: number;
  projectName: string;
  gaps: number;
  exceptions: number;
  lineOfBusiness: string;
  template: string;
  counties?: number;
  specialties?: number;
}

export interface NetworkSubmissionProjectResponse {
  networkSubmissions: NetworkSubmissionProject[];
  totalCount: number;
}

export interface NetworkSubmissionData {
  specialtyGroupCode: string;
  regionSetCode: string;
  key: string;
  customProjectId: number;
  projectName: string;
  projectRunConfigurationId: number;
  regulatorName: string;
  regulatorReviewStatus: SpecialtyGroupExceptionStatus;
  dateSubmitted: string;
  regulatorComments: number;
  gaps: number;
  fillableGaps: number;
  exceptions: number;
  membershipCount: number;
  counties: number;
  specialties: number;
}

export interface NetworkSubmissionTableData {
  key: string;
  regulatorComments: number;
  gaps: number;
  fillableGaps: number;
  membershipCount: number;
  regulatorName: string;
  regulatorReviewStatusBadge: React.ReactNode;
  regulatorReviewStatus: string;
  dateSubmitted: string;
  projectNameLink: React.ReactNode;
  projectName: string;
  counties: number;
  specialties: number;
  exceptionsLink: React.ReactNode;
  exceptions: number;
}

export interface NetworkSubmissionMetricsModel {
  total: number;
  approved: number;
  denied: number;
  pending: number;
  regulatorReviewStatusSegments: {
    label: string;
    value: number;
  }[];
  fillableGapsSegments: {
    label: string;
    value: number;
    color: string;
  }[];
}

export interface AdequacyReviewRowData {
  id: string;
  name: string;
  adequacyIndex: number;
  percentPassBoth: number;
  percentPassOne: number;
  percentPassNone: number;
  percentPassByException: number;
  gapsTotal: number;
  gapsFillable: number;
  exceptionsTotal: number;
  exceptionsApproved: number;
  accessMet: number;
  accessNotMet: number;
  servicingMet: number;
  servicingNotMet: number;
  membershipAtRiskCount: number;
  membershipAtRiskPercent: number;
}

export interface AdequacyReviewTableRow extends AdequacyReviewRowData {
  adequacyIndexString: string;
  adequacyGraph: React.ReactNode;
  membershipAtRiskCountString: string;
  membershipAtRiskPercentString: string;
  [wafflesKeyColumnName]: string;
}

export interface NetworkSubmissionSummary {
  totalGaps: number;
  missingExceptions: number;
}

export interface AdequacyReviewData {
  counties: AdequacyReviewRowData[];
  specialtiesByCounty: { [countyId: string]: AdequacyReviewRowData[] };
  specialties: AdequacyReviewRowData[];
  countiesBySpecialty: { [specialtyId: string]: AdequacyReviewRowData[] };
  summary: NetworkSubmissionSummary;
}

export interface ExceptionGroupRow {
  id: string;
  name: string;
  gaps: number;
  fillable: number;
  exceptionDeficit: number;
}

export interface ExceptionGapRow {
  countyId: string;
  countyName: string;
  specialtyGroupCode: string;
  specialtyGroupDisplayName: string;
  required: number;
  inCounty: number;
  servicing: number;
  met: boolean;
  fillable: boolean;
  gapAge: number;
  hasException: boolean;
}

export interface ExceptionReviewData {
  gaps: ExceptionGapRow[];
}

export interface NetworkSubmissionMetrics {
  approved: number;
  denied: number;
  regulatorReviewStatusAppealed: number;
  regulatorReviewStatusApproved: number;
  regulatorReviewStatusDenied: number;
  regulatorReviewStatusExpired: number;
  regulatorReviewStatusPending: number;
  regulatorReviewStatusSubmitted: number;
  fillableBySingleProviderCount: number;
  unfillableGapsCount: number;
  fillableByMultipleProvidersCount: number;
  pending: number;
  total: number;
}

export interface NetworkSubmissionListItem {
  counties: number;
  countySpecialtyGroups: number;
  customProjectId: number;
  dateSubmitted: string;
  exceptions: number;
  fillableGaps: number;
  gaps: number;
  membershipCount: number;
  projectName: string;
  projectRunConfigurationId: number;
  regulatorComments: number;
  regulatorName: string;
  regulatorReviewStatus: SpecialtyGroupExceptionStatus;
  specialties: number;
  submissionId: number;
}

export interface NetworkProfileChartSummary {
  fillableBySingleProviderCount: number;
  fillableByMultipleProvidersCount: number;
  unfillableGapsCount: number;
  regulatorReviewStatus: SpecialtyGroupExceptionStatus;
}

export interface NetworkProfileMetrics {
  exceptions: number;
  gaps: number;
  networkName: string;
  newRegulatorComments: number;
  customProjectId: number;
}

export interface NetworkProfileOverview {
  adequacyScore: number;
  counties: number;
  dateSubmitted: string;
  description: string;
  lineOfBusiness: string;
  membership: number;
  providerCount: number;
  specialties: number;
  status: SpecialtyGroupExceptionStatus;
}

export interface NetworkSubmissionProjectsQuery {
  pageIndex: number;
  pageSize: number;
  regulatoryReviewStatus?: string;
  searchText?: string;
  serviceAreas?: string[];
  specialties?: string[];
  template?: string;
}

export interface NetworkSubmissionsQuery {
  isArchived: boolean;
  pageIndex: number;
  pageSize: number;
  regulatorClientIds?: number[];
  reviewStatus?: SpecialtyGroupExceptionStatus;
  serviceAreaIds?: string[];
  sortColumn?: string;
  sortDescending?: boolean;
}

export interface NetworkSubmissionMetricsQuery {
  regulatorClientIds?: number[];
  reviewStatus?: SpecialtyGroupExceptionStatus;
  serviceAreaIds?: string[];
  specialtyCodes?: string[];
}

export interface CreateNetworkSubmissionRequest {
  comments: string;
  emailSharedUsers: boolean;
  pendingExceptions: CreateSpecialtyGroupException[];
  projectId?: number;
  regulatorClientId: number;
  sharedUserIds: number[];
  submissionDescription: string;
  submissionName: string;
}

export interface NetworkProfileGapDetailRow {
  countyClass: string;
  countySsa: string;
  customProjectId: number;
  hasException: boolean;
  fillability: AccessGapTypeEnum;
  serviceAreaName: string;
  specialtyGroup: string;
  specialtyGroupCode: string;
  status?: RegionSetStatusType;
}

export interface NetworkProfileGapDetailsResponse {
  items: NetworkProfileGapDetailRow[];
  totalCount: number;
}

export interface GapDetailsQuery {
  pageIndex: number;
  pageSize: number;
  sortColumn?: string;
  sortDescending?: boolean;
}

export enum SubmissionCommentAuthorRole {
  Issuer = 1,
  Regulator = 3,
}

export interface SubmissionCommentDisplay {
  authorName: string | null;
  authorRole: SubmissionCommentAuthorRole;
  commentText: string;
  dateCreated: string;
  isRead: boolean;
  organizationName: string | null;
  submissionCommentId: number;
}

export interface SubmissionCommentListApiResponse {
  comments: SubmissionCommentDisplay[];
}
